import { useState } from "react";
import {
  Dialog,
  DialogBackdrop,
  DialogPanel,
  DialogTitle,
} from "@headlessui/react";
import type DialogOptions from "../types/dialogs/DialogOptions";
import type { DialogState } from "../types/dialogs/DialogState";
import {
  CheckCheckIcon,
  CheckCircleIcon,
  InfoIcon,
  TriangleAlertIcon,
  XCircleIcon,
} from "lucide-react";
import { DialogContext } from "../contexts/DialogContext";
import Button from "../components/ui/button/Button";

export function DialogProvider({ children }: { children: React.ReactNode }) {
  const [dialog, setDialog] = useState<DialogState | null>(null);

  const show = (options: DialogOptions) => {
    setDialog({
      id: crypto.randomUUID(),
      type: options.type ?? "info",
      title: options.title ?? "Notification",
      message: options.message ?? "",
    });
  };

  const confirm = (options: DialogOptions): Promise<boolean> => {
    return new Promise<boolean>((resolve) => {
      setDialog({
        id: crypto.randomUUID(),
        type: "confirm",
        title: options.title ?? "Confirm Action",
        message: options.message ?? "Are you sure?",
        resolve,
      });
    });
  };

  const hide = () => {
    setDialog(null);
  };

  const handleConfirm = () => {
    if (dialog?.resolve) {
      dialog.resolve(true);
    }
    hide();
  };

  const icon = {
    success: <CheckCircleIcon size={20} className="text-green-600" />,
    error: <XCircleIcon size={20} className="text-red-600" />,
    warning: <TriangleAlertIcon size={20} className="text-yellow-500" />,
    info: <InfoIcon size={20} className="text-blue-500" />,
    confirm: <CheckCheckIcon size={20} className="text-blue-500" />,
  }[dialog?.type ?? "info"];

  return (
    <DialogContext.Provider value={{ show, confirm, hide }}>
      {children}

      <Dialog open={!!dialog} onClose={hide}>
        <div
          className={
            "fixed inset-0 flex items-end justify-end p-6 right-7 bottom-3"
          }
        >
          <DialogBackdrop className="fixed inset-0 bg-black/30" />

          <DialogPanel className="fixed inset-0 flex items-center justify-center p-3">
            <DialogTitle className="flex items-center space-x-3 text-lg font-semibold text-gray-900">
              {icon}
              {dialog?.title}
            </DialogTitle>

            <div className="w-full max-w-md rounded bg-white p-4 shadow-xl">
              <div className="mt-4 text-gray-700 text-sm">
                {dialog?.message}
              </div>
              <div className="mt-6 flex justify-end space-x-2">
                {dialog?.type === "confirm" ? (
                  <>
                    <Button onClick={hide} variant="primary" size="sm">
                      Cancel
                    </Button>
                    <Button onClick={handleConfirm} variant="primary" size="sm">
                      Confirm
                    </Button>
                  </>
                ) : (
                  <Button onClick={hide} variant="primary" size="sm">
                    Ok
                  </Button>
                )}
              </div>
            </div>
          </DialogPanel>
        </div>
      </Dialog>
    </DialogContext.Provider>
  );
}
